import { DEFAULT_EMPLOYEE } from '@xeetr/types/defaults'
import { ViewEmployee } from '@xeetr/types/models'
import { copyEmployee } from '@xeetr/types/utils'
import { ReactNode, useMemo, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import AddObjectButton from '../../components/settings/common/AddObjectButton'
import EmployeeForm from '../../components/settings/employees/EmployeeForm'
import EmployeeHeader from '../../components/settings/employees/EmployeeHeader'
import EmployeesFooter from '../../components/settings/employees/EmployeesFooter'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import Loading from '@/components/loading/Loading'
import { useConfirmDialog } from '@/hooks/common/useConfirmDialog'
import { useEmployees } from '@/hooks/useEmployees'
import { useEmployeesMutation } from '@/hooks/useEmployeesMutation'
import { DirtyFormsMap, FormControlHandle } from '@/types/EmployeeForm'
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'

const SortableEmployeeItem = ({ id, children }: { id: string; children: ReactNode }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition: isDragging ? transition : undefined,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
    touchAction: 'manipulation',
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  )
}

const Employees = () => {
  const { employees, isLoading } = useEmployees()
  const { createEmployee, updateEmployee, reorderEmployee, deleteEmployee } = useEmployeesMutation()
  const [orderedEmployees, setOrderedEmployees] = useState<ViewEmployee[] | null>(null)
  const [expandedItems, setExpandedItems] = useState<string[]>([])
  const [activeFormControl, setActiveFormControl] = useState<FormControlHandle | null>(null)
  const [dirtyForms, setDirtyForms] = useState<DirtyFormsMap>(new Map())
  const confirmDialog = useConfirmDialog()

  const handleCreate = () => {
    const newEmployee: ViewEmployee = {
      ...DEFAULT_EMPLOYEE,
      id: uuidv4(),
    }
    createEmployee(newEmployee)
    // Expand the new employee after creation
    setTimeout(() => setExpandedItems([newEmployee.id]), 100)
  }

  const handleCopy = (employee: ViewEmployee) => {
    const copiedEmployee: ViewEmployee = {
      ...copyEmployee(employee),
      id: uuidv4(),
    }
    createEmployee(copiedEmployee)
  }

  const handleDelete = (id: string) => {
    confirmDialog.confirm(
      {
        title: 'Löschen bestätigen!',
        description: 'Möchten Sie den Mitarbeiter wirklich löschen?',
        confirmText: 'Löschen',
        cancelText: 'Abbrechen',
        variant: 'destructive',
      },
      () => {
        deleteEmployee(id)
        // Remove from expanded if was open
        setExpandedItems((prev) => prev.filter((item) => item !== id))
      }
    )
  }

  const sortedEmployees = useMemo(() => {
    const base = orderedEmployees ?? employees
    return [...base].sort((a, b) => a.order - b.order)
  }, [employees, orderedEmployees])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const reorderEmployees = (activeId: string, overId: string): ViewEmployee[] => {
    const oldIndex = sortedEmployees.findIndex((employee) => employee.id === activeId)
    const newIndex = sortedEmployees.findIndex((employee) => employee.id === overId)
    if (oldIndex === -1 || newIndex === -1) return sortedEmployees

    const moved = arrayMove(sortedEmployees, oldIndex, newIndex)
    return moved.map((employee, index) => ({ ...employee, order: index }))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const updatedEmployees = reorderEmployees(active.id as string, over.id as string)
    setOrderedEmployees(updatedEmployees)

    updatedEmployees.forEach((employee) => {
      if (employee.order !== sortedEmployees.find((item) => item.id === employee.id)?.order) {
        reorderEmployee({ id: employee.id, employee: { order: employee.order } })
      }
    })
  }

  /**
   * Handle accordion value change with dirty form warning
   * If user tries to close a dirty form, show confirmation dialog
   */
  const handleAccordionChange = (newExpandedItems: string[]) => {
    const closingItems = expandedItems.filter((item) => !newExpandedItems.includes(item))

    // Check if any closing item has dirty form
    const dirtyClosingItem = closingItems.find((item) => dirtyForms.get(item))

    if (dirtyClosingItem) {
      // Show warning dialog
      confirmDialog.confirm(
        {
          title: 'Nicht gespeicherte Änderungen',
          description:
            'Sie haben nicht gespeicherte Änderungen. Möchten Sie diese verwerfen und fortfahren?',
          confirmText: 'Verwerfen',
          cancelText: 'Abbrechen',
          variant: 'destructive',
        },
        () => {
          // User confirmed, allow close and discard changes
          setExpandedItems(newExpandedItems)
          setDirtyForms((prev) => {
            const next = new Map(prev)
            next.delete(dirtyClosingItem)
            return next
          })
        }
      )
    } else {
      // No dirty forms, allow close
      setExpandedItems(newExpandedItems)
    }
  }

  /**
   * Register/unregister form control from child EmployeeForm
   * Updates dirty state tracking
   */
  const handleRegisterForm = (handle: FormControlHandle | null) => {
    setActiveFormControl(handle)

    // Update dirty state map
    if (handle) {
      setDirtyForms((prev) => {
        const next = new Map(prev)
        next.set(handle.employeeId, handle.isDirty)
        return next
      })
    }
  }

  return (
    <div className="flex flex-col">
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Mitarbeiter</h2>
        <p className="text-muted-foreground text-sm">
          Verwalten Sie die Mitarbeiter Ihres Unternehmens, um Einstellungen und Arbeitszeiten zu
          organisieren.
        </p>
      </div>
      <div className="flex justify-end">
        <AddObjectButton append={handleCreate} label="Neuer Mitarbeiter:in" />
      </div>
      <Loading loading={isLoading} label="Mitarbeiter werden geladen...">
        <Accordion
          type="multiple"
          className="mt-4"
          value={expandedItems}
          onValueChange={handleAccordionChange}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortedEmployees.map((employee) => employee.id)}
              strategy={verticalListSortingStrategy}
            >
              {sortedEmployees.map((employee, employeeIndex) => (
                <SortableEmployeeItem key={employee.id} id={employee.id}>
                  <AccordionItem key={employee.id} value={employee.id}>
                    <AccordionTrigger>
                      <EmployeeHeader employee={employee} employeeIndex={employeeIndex} />
                    </AccordionTrigger>
                    <AccordionContent>
                      <EmployeeForm
                        employee={employee}
                        isExpanded={expandedItems.includes(employee.id)}
                        onRegisterForm={handleRegisterForm}
                        onCopy={() => handleCopy(employee)}
                        onDelete={() => handleDelete(employee.id)}
                        onUpdate={(updatedEmployee) =>
                          updateEmployee({ id: employee.id, employee: updatedEmployee })
                        }
                      />
                    </AccordionContent>
                  </AccordionItem>
                </SortableEmployeeItem>
              ))}
            </SortableContext>
          </DndContext>
        </Accordion>
      </Loading>
      <ConfirmDialog
        open={confirmDialog.isOpen}
        onOpenChange={confirmDialog.setIsOpen}
        title={confirmDialog.options.title}
        description={confirmDialog.options.description}
        confirmText={confirmDialog.options.confirmText}
        cancelText={confirmDialog.options.cancelText}
        variant={confirmDialog.options.variant}
        onConfirm={confirmDialog.handleConfirm}
      />
      <EmployeesFooter formControl={activeFormControl} />
    </div>
  )
}

export default Employees
